if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');
      // The slider itself carries data-section-id, so this.parent is usually the
      // slider; the section wrapper (hidden until loaded, see products-feed) is .product-feed
      this.feedSection = this.closest('.product-feed');
      this.sliderOptions = {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: swiperOptions.slidesPerViewMobile || 'auto',
        resistanceRatio: 0.72,
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };
      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }
      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }
      if (swiperOptions.loop) {
        this.sliderOptions.loop = swiperOptions.loop;
      }
    }

    connectedCallback() {
      // Product recommendations start hidden (never shown empty), so they can't be
      // observed for visibility — fetch once the page is idle instead.
      if (this.requiresFetch && this.feedSection?.classList.contains('hidden')) {
        const run = () => this.fetchItems();
        const whenIdle = () =>
          'requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 200);
        if (document.readyState === 'complete') {
          whenIdle();
        } else {
          window.addEventListener('load', whenIdle, { once: true });
        }
        return;
      }

      // These are recommendation-carousel rows (featured products, shop the
      // look, etc.) — usually below the fold, so defer Swiper initialization
      // (and the fetch, if any) until the row is about to be seen, instead
      // of paying the CPU cost for every row on initial load.
      this.observer = new IntersectionObserver((entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.disconnect();

          if (this.requiresFetch) {
            this.fetchItems();
            continue;
          }
          this.initSlider();
          this.syncHeights();
          window.addEventListener("resize", () => this.syncHeights());
        }
      });
      this.observer.observe(this);
    }

    syncHeights() {
      const textSlide = this.querySelector('.products-feed__card-text-custom-slide');
      const actionElement = this.querySelector('.product-card__actions');

      if (textSlide && actionElement) {
        const actionHeight = actionElement.offsetHeight;
        textSlide.style.height = `${actionHeight}px`;
      }
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');
      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(
            text,
            'text/html'
          );
          const wrapper = html.querySelector('.swiper-wrapper');

          if (wrapper?.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML =
              wrapper.innerHTML;
            this.topUpWithFallback();
            this.parent.classList.remove('hidden');
            this.feedSection?.classList.remove('hidden');
            this.initSlider();
          } else {
            this.useFallbackSlides();
          }
        })
        .catch(e => {
          console.error(e);
          this.useFallbackSlides();
        });
    }

    // Too few recommendations: fill up to a full desktop row (4) with fallback slides, skipping duplicates
    topUpWithFallback(minSlides = 4) {
      const fallback = this.querySelector('template[data-fallback-slides]');
      if (!fallback) return;

      const wrapper = this.querySelector('.swiper-wrapper');
      const productPath = slide =>
        slide.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('?')[0];
      const present = new Set([...wrapper.children].map(productPath));

      for (const slide of fallback.content.children) {
        if (wrapper.children.length >= minSlides) break;
        const path = productPath(slide);
        if (!path || present.has(path)) continue;
        wrapper.appendChild(slide.cloneNode(true));
        present.add(path);
      }
    }

    // No recommendations: show the server-rendered fallback (e.g. same collection), if any
    useFallbackSlides() {
      const fallback = this.querySelector('template[data-fallback-slides]');
      if (!fallback || !fallback.innerHTML.trim().length) return;

      this.querySelector('.swiper-wrapper').innerHTML = fallback.innerHTML;
      this.feedSection?.classList.remove('hidden');
      this.initSlider();
    }

    initSlider(slider = this) {
      loadSwiperLibrary().then(() => {
        this.slider = new Swiper(slider, this.sliderOptions);

        this.slider.wrapperEl.setAttribute('aria-live', 'polite');
        this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;
        this.slider.slides.forEach((slide, i) => {
          slide.setAttribute('role', 'group');
          slide.setAttribute(
            'aria-label',
            `${i + 1} / ${this.slider.slides.length}`
          );
          slide.dataset.index = i;
        });

        if (this.swiperOptions?.navigation) {
          const sliderButtons = [
            this.slider.navigation.prevEl,
            this.slider.navigation.nextEl
          ];

          sliderButtons.forEach((button, i) => {
            let type = 'Previous';

            if (i === 1) {
              type = 'Next';
            }

            button.setAttribute('aria-label', `${type} slide`);
            button.setAttribute(
              'aria-controls',
              this.slider.wrapperEl.id
            );
          });
        }

        this.slider.wrapperEl.addEventListener('focusin', e => {
          this.slider.el.scrollLeft = 0;

          const slide = e.target.closest('.swiper-slide');
          const slideIndex = +slide.dataset.index;
          const isFirstSlide = slideIndex === 0;
          const isLastSlide =
            slideIndex === this.slider.slides.length - 1;

          if (isFirstSlide || isLastSlide) {
            this.slider.slideTo(slideIndex, 0);

            return;
          }

          const slideRect = slide.getBoundingClientRect();
          const wrapperWidth = this.slider.wrapperEl.clientWidth;
          // prettier-ignore
          const slideLeft = slideRect.x + slideRect.width - ((document.body.clientWidth - wrapperWidth) / 2);
          const isSlideVisible =
            slideLeft <= wrapperWidth && slideLeft >= slideRect.width;

          if (isSlideVisible) {
            return;
          }

          slideLeft >= slideRect.width
            ? this.slider.slideNext(0)
            : this.slider.slidePrev(0);
        });

        const swiperOptions = JSON.parse(this.getAttribute('data-swiper-options')) || {};
        /** stop / start autoplay on hover if quick cart is not open */
        this.slider.wrapperEl.addEventListener('mouseenter', () =>
          this.slider.autoplay.stop()
        );
        this.slider.wrapperEl.addEventListener('mouseleave', () => {
          if (!this.classList.contains('product--open-on-quick-cart') && this.sliderOptions.autoplay) {
            this.slider.autoplay.start();
          }
          if (swiperOptions.autoplay === false) {
            this.slider.autoplay.stop();
          }
        });

        if (
          this.hasAttribute('data-init-quick-cart') &&
          document.querySelector('quick-cart-drawer') &&
          this.querySelector('.swiper-slide')
        ) {
          document.querySelector('quick-cart-drawer').init();
        }

        for (const card of this.querySelectorAll('product-card')) {
          card.init();
        }

        if (Shopify.designMode) {
          if (
            this.hasAttribute('data-init-quick-cart') &&
            document.querySelector('quick-cart-drawer') &&
            this.querySelector('.swiper-slide')
          ) {
            document.querySelector('quick-cart-drawer').init();
          }
        }
      });
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}
